import React, { useContext } from 'react';
import { useDragLayer, useDrop } from 'react-dnd';
import tinycolor from 'tinycolor2';

import StatusCard from '@totem/components/ticketing/details/tasks/kanban/statusCard';
import TaskContext from '@totem/components/ticketing/details/tasks/taskContext';
import {
  Task,
  TASK_TYPE_GROUP,
  TASK_TYPE_TASK,
} from '@totem/components/ticketing/ticketing';
import NaPartyInvolvedContext from '@totem/components/unauthenticated/ticketing/details/partyInvolved/naPartyInvolvedContext';
import NaTaskCard from '@totem/components/unauthenticated/ticketing/details/tasks/naTaskCard';
import { isNotNull } from '@totem/utilities/common';

type Props = {
  status: string;
  color: string;
};

const NaStatusColumn = ({ status, color }: Props) => {
  const { partyInvolved } = useContext(NaPartyInvolvedContext);
  const { tasks, taskWorkflow, onTaskStatusChange } = useContext(TaskContext);

  const onDrop = (task: Task) => {
    const currentStatus = taskWorkflow.find((chk) => chk.code === task.status);
    if (isNotNull(currentStatus)) {
      if (currentStatus.options.includes(status)) {
        // Send Update Task Status
        onTaskStatusChange(status, task);
      }
    }
  };

  const [, dropRef] = useDrop({
    accept: TASK_TYPE_TASK,
    drop: (item: Task) => {
      onDrop(item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }), // Are we hovering this drop zone
      isDragging: !!monitor.getItemType(), // Is *any* drag happening
    }),
  });

  const coreStatuses = [
    'PENDING',
    'SCHEDULED',
    'IN PROGRESS',
    'REVIEW',
    'COMPLETED',
  ];
  const statusTask = tasks.filter(
    (chk) =>
      chk.status === status &&
      chk.type !== TASK_TYPE_GROUP &&
      chk.assignedResources.includes(partyInvolved.id),
  );

  const isDraggingTasks = useDragLayer((monitor) => {
    const isTaskDragging =
      monitor.isDragging() && monitor.getItemType() === TASK_TYPE_TASK;
    let isStatusDroppable = false;
    if (isTaskDragging) {
      const taskItem = monitor.getItem<Task>();

      const currentStatus = taskWorkflow.find(
        (chk) => chk.code === taskItem.status,
      );
      if (isNotNull(currentStatus)) {
        if (currentStatus.options.includes(status)) {
          isStatusDroppable = true;
        }
      }
    }

    return isTaskDragging && isStatusDroppable;
  });

  const background = isDraggingTasks
    ? tinycolor.mix(color, '#FFFFFF', 80).toHexString()
    : null;

  return (
    <>
      {isNotNull(statusTask) &&
        (statusTask.length > 0 ||
          coreStatuses.includes(status) ||
          isDraggingTasks) && (
          <div
            style={{ marginBottom: '16px', backgroundColor: background }}
            ref={dropRef}
          >
            <StatusCard status={status} color={color} />
            {statusTask.map((tsk) => (
              <NaTaskCard key={tsk.id} color={color} task={tsk} />
            ))}
            {isDraggingTasks && <div style={{ height: '32px' }} />}
          </div>
        )}
    </>
  );
};

export default NaStatusColumn;
